import type { ReviewDecisionInput } from './review.js';

// BULK OPERATIONS on portal assets (2026-10-13). PURE — shared by the server route and the staff UI.
//
// ⚠️⚠️ BULK IS A LOOP OVER THE SINGLE-ASSET SERVICES, NEVER A BATCH WRITE. server/modules/portalAssets/
// bulk.ts calls reviewAsset / deleteAsset / updateAssetMetadata once per asset. Each already does ONE
// transaction carrying the change, the counter move and the audit entry; a batch variant would have to
// re-implement all three, and the counters are where a second implementation drifts. It also gives
// partial failure for free: each asset succeeds or fails on its own.
//
// ⚠️⚠️ ONE AUDIT ENTRY PER ASSET, NEVER ONE PER BATCH — a per-batch entry cannot answer "who approved
// THIS photo". That falls out of the loop; there is no code here that could write a summary entry.
//
// THE OPERATIONS. The old plan said "move to category". Assets have MANY categories (2026-10-10), so
// "move" means nothing: the operations are add-to and remove-from.

export type BulkOperation =
  | { op: 'review'; decision: ReviewDecisionInput['decision']; reason?: string | null }
  | { op: 'delete' }
  | { op: 'addCategory'; categoryId: string }
  | { op: 'removeCategory'; categoryId: string }
  /** Run E: clear title, tags and categories (and the AI record). Staff only. ⚠️ NOT enrichment: it calls no model and costs nothing. */
  | { op: 'clear' }
  /** ⚠️ AI enrichment (Run D). Reached ONLY through POST /bulk-enrich — bulkRequestSchema deliberately does not accept it. */
  | { op: 'enrich' };

/**
 * ⚠️ THE CAP: 50 assets per request. This is an INTERACTIVE request doing N SEQUENTIAL transactions, held
 * open behind Cloudflare's ~100s origin timeout — not a background sweep (the purge sweep's 200 is a
 * background figure and is too high here). Sequential is deliberate: one account's counter doc is touched by
 * every one of its assets, so concurrent transactions would contend and retry. 50 matches the list page
 * size and the upload cap, so "select this whole page" works without a second rule to learn. Expected cost
 * per asset is one Firestore read+write transaction (tens to low hundreds of ms — NOT measured live), i.e.
 * seconds for 50; BULK_BUDGET_MS below is the safety net, not the expectation.
 */
export const BULK_MAX_ASSETS = 50;

/**
 * Stop STARTING new assets after this long, well inside the ~100s proxy timeout, and report the rest as not
 * attempted rather than let the request die and lose the whole result.
 */
export const BULK_BUDGET_MS = 60_000;

export type BulkAssetResult =
  /** `note`: something worth saying about a success — e.g. enrichment kept the title a person had set. */
  | { assetId: string; outcome: 'ok'; note?: string }
  | { assetId: string; outcome: 'failed'; status: number; message: string }
  /** The time budget ran out before this one started. NOTHING was written for it. */
  | { assetId: string; outcome: 'notAttempted'; message: string };

export type BulkResponse = { results: BulkAssetResult[] };

export const NOT_ATTEMPTED_MESSAGE = 'Not attempted — the request ran out of time before it reached this one. Select it again.';

const PAST: Record<Exclude<BulkOperation['op'], 'review'>, string> = {
  enrich: 'enriched',
  delete: 'deleted',
  addCategory: 'added to the category',
  removeCategory: 'removed from the category',
  clear: 'cleared',
};

/** What "done" means for this operation, in the words a person reads ("approved", "not approved", "deleted"…). */
export function pastTense(op: BulkOperation): string {
  if (op.op === 'review') return op.decision === 'approved' ? 'approved' : 'not approved';
  return PAST[op.op];
}

export type BulkSummary = {
  requested: number;
  succeeded: number;
  failed: number;
  notAttempted: number;
  /** "43 of 50 approved." — ALWAYS the count and the total, so a partial run cannot read as a clean one. */
  headline: string;
  /** One line per asset that did NOT go through, named by the caller's `nameOf` (filename). */
  problems: string[];
  /** One line per asset that succeeded WITH something to say (see BulkAssetResult.note). */
  notes: string[];
  /** true only when every requested asset succeeded. */
  complete: boolean;
};

export function summariseBulk(
  op: BulkOperation,
  results: readonly BulkAssetResult[],
  nameOf: (assetId: string) => string,
): BulkSummary {
  const succeeded = results.filter((r) => r.outcome === 'ok').length;
  const failed = results.filter((r) => r.outcome === 'failed').length;
  const notAttempted = results.filter((r) => r.outcome === 'notAttempted').length;
  const requested = results.length;
  const complete = requested > 0 && succeeded === requested;
  const headline =
    requested === 0
      ? 'Nothing was selected.'
      : `${succeeded} of ${requested} ${pastTense(op)}.${failed ? ` ${failed} failed.` : ''}${notAttempted ? ` ${notAttempted} not attempted.` : ''}`;
  const problems = results.flatMap((r) => (r.outcome === 'ok' ? [] : [`${nameOf(r.assetId)} — ${r.message}`]));
  const notes = results.flatMap((r) => (r.outcome === 'ok' && r.note ? [`${nameOf(r.assetId)} — ${r.note}`] : []));
  return { requested, succeeded, failed, notAttempted, headline, problems, notes, complete };
}

/** Toggle one id. At the cap, adding refuses (the same set comes back) and `atCap` says so. */
export function toggleSelection(selected: ReadonlySet<string>, id: string): { next: Set<string>; atCap: boolean } {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
    return { next, atCap: false };
  }
  if (next.size >= BULK_MAX_ASSETS) return { next, atCap: true };
  next.add(id);
  return { next, atCap: false };
}

/** Select every visible id up to the cap; reports how many were left out. */
export function selectVisible(visible: readonly string[], selected: ReadonlySet<string>): { next: Set<string>; leftOut: number } {
  const next = new Set(selected);
  let leftOut = 0;
  for (const id of visible) {
    if (next.has(id)) continue;
    if (next.size >= BULK_MAX_ASSETS) leftOut += 1;
    else next.add(id);
  }
  return { next, leftOut };
}

/**
 * What the bulk-delete confirmation says. Pure, because every clause is a promise someone acts on, and it
 * mirrors the single delete's (deleteConfirmation): a delete is NOT a takedown, and an approved asset may
 * already be in use. `approved` is how many of the selection are approved.
 */
export function bulkDeleteConfirmation(
  count: number,
  approved: number,
  retentionDays: number,
): { title: string; warning: string | null; body: string[] } {
  const noun = count === 1 ? 'asset' : 'assets';
  return {
    title: `Delete ${count} ${noun}?`,
    warning:
      approved > 0
        ? `${approved} of these ${approved === 1 ? 'is' : 'are'} approved and may already be in use. Deleting stops ${approved === 1 ? 'it' : 'them'} being used from now on; it does not take anything down from what is already published.`
        : null,
    body: [
      `They leave the clubs' portals straight away and stop being usable. Each can be restored from its club's Recently deleted for ${retentionDays} days.`,
      '⚠️ The files themselves are NOT removed, and links already issued keep working until their window ends. If a file must be gone now, use Purge now on that asset — a delete is not a takedown.',
    ],
  };
}

/**
 * What a CLUB's bulk-delete confirmation says (Run F). ⚠️ Deletion is soft for `retentionDays` and ONLY KYCK CAN RESTORE:
 * a club cannot undo this itself, so the sentence says so before the click. That is the difference between a surprise and a
 * decision. An approved asset may already be in use, same warning as the staff one.
 */
export function clientBulkDeleteConfirmation(
  count: number,
  approved: number,
  retentionDays: number,
): { title: string; warning: string | null; body: string[] } {
  const noun = count === 1 ? 'asset' : 'assets';
  return {
    title: `Delete ${count} ${noun}?`,
    warning:
      approved > 0
        ? `${approved} of these ${approved === 1 ? 'is' : 'are'} approved and may already be in use. Deleting stops ${approved === 1 ? 'it' : 'them'} being used from now on.`
        : null,
    body: [
      `Deleted assets are kept for ${retentionDays} days. You can't restore them yourself: ask Kyck to restore one.`,
    ],
  };
}

/**
 * What a CLEAR would destroy, counted from the STORED provenance (server/modules/portalAssets/bulkClear.ts). ⚠️ A
 * PREVIEW: advisory, read before the action, so an edit landing in between is not reflected. The clear itself acts on
 * whatever is there when it runs.
 */
export type ClearPreview = {
  /** Assets read. */
  assets: number;
  /** Assets with nothing to clear at all (no title, no tags, no categories, no AI record). */
  alreadyEmpty: number;
  /** Assets where a PERSON set at least one of title / tags / categories: gone for good, no regenerating it. */
  withHumanValues: number;
  /** Of those, how many have each field set by a person. */
  humanTitles: number;
  humanTags: number;
  humanCategories: number;
};

/**
 * THE CLEAR CONFIRMATION (Run E section 6 — MY wording, the architect's recommendation, open to disagreement). This is
 * the one bulk action with NO undo, and the two kinds of value are not alike: AI output regenerates, a title a person
 * typed is gone. So rather than withholding it from clubs or splitting it in two, the confirmation STATES THE EXPOSURE —
 * the provenance recorded in Run D is what makes the number possible. Pure: every clause is a promise someone acts on.
 */
export function bulkClearConfirmation(
  count: number,
  preview: ClearPreview | null,
): { title: string; warning: string; body: string[]; destructive: boolean } {
  const noun = count === 1 ? 'asset' : 'assets';
  const title = `Clear the title, tags and categories on ${count} ${noun}?`;
  if (preview === null) {
    // ⚠️ The count could not be read. Never confirm blind as if nothing human were at stake.
    return {
      title,
      warning: 'We could not check how many of these were written by a person. Anything a person wrote cannot be recovered.',
      body: CLEAR_BODY,
      destructive: true,
    };
  }
  const h = preview.withHumanValues;
  const warning =
    h === 0
      ? 'Everything on these was written by AI, so AI enrich can regenerate it.'
      : `${h} of these ${h === 1 ? 'has' : 'have'} a title, tags or categories set by a person, and that cannot be recovered.` +
        ` (${[
          preview.humanTitles > 0 ? `${preview.humanTitles} ${preview.humanTitles === 1 ? 'title' : 'titles'}` : null,
          preview.humanTags > 0 ? `${preview.humanTags} with tags` : null,
          preview.humanCategories > 0 ? `${preview.humanCategories} with categories` : null,
        ]
          .filter((x): x is string => x !== null)
          .join(', ')})`;
  return { title, warning, body: CLEAR_BODY, destructive: h > 0 };
}

const CLEAR_BODY = [
  'This removes the title (the asset goes back to sorting and searching by its file name), every tag and every category, and the record that AI ever touched it. There is no undo.',
  'It does not call the AI and costs nothing. Run AI enrich afterwards to fill them in again.',
];
